import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API } from "@/App";

/**
 * SEO meta tag component that fetches and renders meta tags for a page path.
 * Directly modifies DOM tags in addition to Helmet so extensions and crawlers
 * immediately see updated metadata without lag.
 */
export default function PageSeo({ path, fallbackTitle = "Jamadhan", fallbackDescription = "" }) {
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    if (!path) return;
    let isMounted = true;
    axios.get(`${API}/seo/page`, { params: { path } })
      .then(res => {
        if (!isMounted) return;
        if (res.data && (res.data.seo_title || res.data.seo_description)) {
          setSeo(res.data);
        }
      })
      .catch(() => {});

    return () => { isMounted = false; };
  }, [path]);

  const title = seo?.seo_title || fallbackTitle;
  const description = seo?.seo_description || fallbackDescription;
  const canonical = seo?.canonical_url || `https://jamadhan.in${path === '/' ? '' : path}`;
  const robotsContent = `${seo?.robots_index !== false ? 'index' : 'noindex'}, ${seo?.robots_follow !== false ? 'follow' : 'nofollow'}`;
  const ogTitle = seo?.og_title || title;
  const ogDesc = seo?.og_description || description;
  const ogImage = seo?.og_image || "";
  const twitterTitle = seo?.twitter_title || ogTitle;
  const twitterDesc = seo?.twitter_description || ogDesc;
  const twitterImage = seo?.twitter_image || ogImage;

  // Direct DOM manipulation guarantees that browser extensions (e.g. Detailed SEO Extension)
  // and head scanners immediately see the live updated title and description.
  useEffect(() => {
    if (title && typeof document !== "undefined") {
      document.title = title;
    }

    if (description && typeof document !== "undefined") {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
    }

    if (canonical && typeof document !== "undefined") {
      let linkCanon = document.querySelector('link[rel="canonical"]');
      if (!linkCanon) {
        linkCanon = document.createElement('link');
        linkCanon.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanon);
      }
      linkCanon.setAttribute('href', canonical);
    }
  }, [title, description, canonical]);

  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsContent} />
      {canonical && <link rel="canonical" href={canonical} />}
      {seo?.focus_keyword && <meta name="keywords" content={[seo.focus_keyword, ...(seo.secondary_keywords || [])].join(", ")} />}

      {/* Open Graph */}
      <meta property="og:title" content={ogTitle} />
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitle} />
      {twitterDesc && <meta name="twitter:description" content={twitterDesc} />}
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}

      {/* Schema */}
      {seo?.schema_markup && (
        <script type="application/ld+json">{typeof seo.schema_markup === 'string' ? seo.schema_markup : JSON.stringify(seo.schema_markup)}</script>
      )}
    </Helmet>
  );
}
